"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Compass, ReceiptText, Star, Check, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface Pillar {
  id: string;
  title: string;
  badge: string;
  summary: string;
  detail1: string;
  detail2: string;
  detail3: string;
  iconName: "shield" | "compass" | "receipt" | "star";
}
const pillars: Pillar[] = [{
  id: "experience",
  title: "Years of local experience",
  badge: "Brookhaven Native",
  summary: "Deep architectural understanding of local neighborhoods and zoning codes.",
  detail1: "Historic Ashford Park & Brookhaven Village experience",
  detail2: "Permit navigation with local city planning boards",
  detail3: "Vetted trade partnerships across North Atlanta",
  iconName: "compass"
}, {
  id: "pricing",
  title: "Transparent pricing",
  badge: "Itemized Scopes",
  summary: "Written scopes and fixed milestones before demo begins.",
  detail1: "Detailed line-item material and labor estimates",
  detail2: "Milestone-based progress draws tied to approvals",
  detail3: "No hidden surprise allowances or unapproved changes",
  iconName: "receipt"
}];
export default function WhyChoose() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 15
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const renderIcon = (name: Pillar["iconName"]) => {
    switch (name) {
      case "shield":
        return <ShieldCheck className="size-6 text-primary" strokeWidth={1.75} />;
      case "compass":
        return <Compass className="size-6 text-primary" strokeWidth={1.75} />;
      case "receipt":
        return <ReceiptText className="size-6 text-primary" strokeWidth={1.75} />;
      case "star":
        return <Star className="size-6 text-primary" strokeWidth={1.75} />;
    }
  };
  return <section id="why-choose-us" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-24 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 md:px-10 lg:px-12">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1.5 text-xs font-semibold tracking-wider text-primary uppercase">
            <span>Why Brookhaven chooses us</span>
          </div>

          <h2 className="mt-5 font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Remodeling built on local trust and clear milestones
          </h2>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            We take the guesswork out of home renovations in Brookhaven with steady communication and honest estimates.
          </p>

          <div className="mx-auto mt-6 h-[1px] w-24 bg-primary/40" />
        </div>

        {/* Core Pillars Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {pillars.map((pillar, i) => <motion.div key={pillar.id} data-index={i} variants={itemVariants} whileHover={shouldReduceMotion ? undefined : {
          y: -4
        }} className="group relative flex flex-col justify-between rounded-2xl border border-border/80 bg-card p-6 shadow-sm backdrop-blur-md transition-[border-color,box-shadow] duration-250 hover:border-primary/50 hover:shadow-md md:p-7">
              <div>
                {/* Top Badge & Architectural Icon */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex size-12 items-center justify-center rounded-xl border border-primary/25 bg-primary/10 transition-colors duration-200 group-hover:bg-primary/15">
                    {renderIcon(pillar.iconName)}
                  </div>
                  <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary text-[11px] font-semibold tracking-wider uppercase">
                    {pillar.badge}
                  </Badge>
                </div>

                {/* Title & Short Summary */}
                <h3 className="mt-6 font-serif text-xl font-semibold text-foreground">
                  {pillar.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {pillar.summary}
                </p>

                {/* Separator */}
                <div className="my-5 h-[1px] w-full bg-border/60" />

                {/* Bulleted Points */}
                <ul className="space-y-2.5 text-xs text-foreground/90">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                      <Check className="size-2.5" strokeWidth={2.5} />
                    </span>
                    <span>{pillar.detail1}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                      <Check className="size-2.5" strokeWidth={2.5} />
                    </span>
                    <span>{pillar.detail2}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                      <Check className="size-2.5" strokeWidth={2.5} />
                    </span>
                    <span>{pillar.detail3}</span>
                  </li>
                </ul>
              </div>

              {/* Bottom Subtle Indicator */}
              <div className="mt-6 pt-4 border-t border-border/40 flex items-center justify-between text-xs font-medium text-muted-foreground">
                <span className="text-[11px] tracking-wide uppercase text-primary/90 font-semibold">
                  Verified standard
                </span>
                <span className="text-muted-foreground/60">0{i + 1}</span>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Bottom Callout Strip */}
        <div className="mt-14 rounded-2xl border border-border/70 bg-card/60 p-6 md:p-8 backdrop-blur-sm">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div className="max-w-2xl">
              <h4 className="font-serif text-lg font-semibold text-foreground md:text-xl">
                Ready to review your Brookhaven renovation project?
              </h4>
              <p className="mt-1 text-sm text-muted-foreground">
                Schedule a consultation to discuss your architectural plans, timeline, and an honest scope of work.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <Button asChild variant="default" size="lg" className="rounded-lg font-medium shadow-xs">
                <Link to="/ContactRequestAnEstimatePage">
                  <span>Schedule consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}